import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import { useEffect, useMemo, useState } from "react";
import { useReport } from "../../../tools/api";
import { faDigits } from "../../../tools/tools";
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const SERIES = [
	{ key: "sp", label: "سیم‌کارت", color: "#e64225" },
	{ key: "ep", label: "بسته", color: "#37b399" },
	{ key: "dc", label: "شارژ", color: "#61aaff" },
];
// the three services disagree on the date format: "2024-09-10" or "10/1/2023 12:00:00 AM"
export const dayKey = (value) => {
	const s = String(value ?? "");
	let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (m) return `${m[1]}-${m[2]}-${m[3]}`;
	m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
	return m ? `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}` : null;
};
const fa = (opts) => new Intl.DateTimeFormat("fa-IR-u-ca-persian", opts);
const shortDay = fa({ year: "numeric", month: "2-digit", day: "2-digit" });
// assembled from parts: browsers order the full Persian pattern inconsistently ("۱۴۰۲ مهر ۹, یکشنبه")
const longParts = fa({ weekday: "long", day: "numeric", month: "long", year: "numeric" });
const longDay = {
	format: (d) => {
		const p = Object.fromEntries(longParts.formatToParts(d).map((x) => [x.type, x.value]));
		return `${p.weekday} ${p.day} ${p.month} ${p.year}`;
	},
};
// "2024-09-10" -> local Date (no timezone shift)
const toDate = (k) => new Date(+k.slice(0, 4), +k.slice(5, 7) - 1, +k.slice(8, 10));

// { "2026-10-12": { sp, ep, dc } } from the three responses
const toDaily = (responses) => {
	const daily = {};
	SERIES.forEach(({ key }) =>
		(responses[key] ?? []).forEach((item) => {
			const k = dayKey(item.date);
			if (!k) return;
			daily[k] ??= { sp: 0, ep: 0, dc: 0 };
			daily[k][key] += Number(item.count) || 0;
		}),
	);
	return daily;
};

// every day the services report, oldest first (PMO: show the data as it is, no date window)
export const buildChart = (daily) =>
	Object.keys(daily)
		.sort()
		.map((k) => ({ label: shortDay.format(toDate(k)), title: longDay.format(toDate(k)), ...daily[k] }));

const dayCount = (b) => b.sp + b.ep + b.dc;

const ChartPurchase = () => {
	const { statisticalReportDCProductCountSales, statisticalReportEPProductCountSales, statisticalReportSPProductCountSales } = useReport();
	const [daily, setDaily] = useState(null);

	useEffect(() => {
		const load = async () => {
			const [sp, ep, dc] = await Promise.all([
				statisticalReportSPProductCountSales(),
				statisticalReportEPProductCountSales(),
				statisticalReportDCProductCountSales(),
			]);
			// a failed service just contributes nothing instead of hiding the whole chart
			const ok = (r) => (r && !r.HasError ? r.Result : []);
			setDaily(toDaily({ sp: ok(sp), ep: ok(ep), dc: ok(dc) }));
		};
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const buckets = useMemo(() => (daily ? buildChart(daily) : null), [daily]);

	return (
		<section className="dash-card dash-chart" aria-labelledby="chart-title">
			<header className="dash-card-head">
				<h2 id="chart-title">تعداد فروش روزانه</h2>
			</header>

			{buckets && (
				<>
					{buckets.length > 0 && (
						<dl className="chart-stats">
							<div>
								<dt>جمع فروش</dt>
								<dd>
									<strong>{faDigits(buckets.reduce((sum, b) => sum + dayCount(b), 0))}</strong> مورد
								</dd>
							</div>
							<div>
								<dt>بهترین روز</dt>
								<dd>
									{(() => {
										const best = buckets.reduce((a, b) => (dayCount(b) > dayCount(a) ? b : a));
										return `${best.title} — ${faDigits(dayCount(best))} مورد`;
									})()}
								</dd>
							</div>
							<div>
								<dt>آخرین فروش</dt>
								<dd>{buckets[buckets.length - 1].title}</dd>
							</div>
						</dl>
					)}
					{buckets.length === 0 ? (
						<p className="chart-empty">فروشی ثبت نشده است.</p>
					) : (
						<div className="dash-chart-body">
							<Bar
								data={{
									labels: buckets.map((b) => b.label),
									datasets: SERIES.map((s) => ({
										label: s.label,
										data: buckets.map((b) => b[s.key]),
										backgroundColor: s.color,
										borderRadius: 4,
										borderSkipped: false,
										maxBarThickness: 28,
										stack: "sales",
									})),
								}}
								options={{
									responsive: true,
									maintainAspectRatio: false,
									interaction: { mode: "index", intersect: false },
									plugins: {
										legend: {
											position: "top",
											align: "end",
											rtl: true,
											labels: { usePointStyle: true, pointStyle: "rectRounded", boxHeight: 8, padding: 16, font: { family: "IRANSansX" } },
										},
										tooltip: {
											rtl: true,
											padding: 10,
											titleFont: { family: "IRANSansX" },
											bodyFont: { family: "IRANSansX" },
											footerFont: { family: "IRANSansX" },
											filter: (item) => item.raw > 0,
											callbacks: {
												title: (items) => (items.length ? buckets[items[0].dataIndex].title : ""),
												label: (ctx) => ` ${ctx.dataset.label}: ${faDigits(ctx.raw)}`,
												footer: (items) => `جمع: ${faDigits(items.reduce((s, i) => s + i.raw, 0))}`,
											},
										},
									},
									scales: {
										x: {
											stacked: true,
											grid: { display: false },
											// every date on desktop; narrow screens thin them out instead of overlapping
											ticks: { autoSkip: window.innerWidth < 768, maxRotation: 60, font: { family: "IRANSansX" } },
										},
										y: {
											stacked: true,
											beginAtZero: true,
											border: { display: false },
											grid: { color: "#f0f0f0" },
											ticks: { precision: 0, callback: (v) => faDigits(v), font: { family: "IRANSansX" } },
										},
									},
								}}
							/>
						</div>
					)}
				</>
			)}
		</section>
	);
};

export default ChartPurchase;
